/**
 * crm/HubspotSyncCard.jsx — Feb 2026
 * Status + manual "Sync now" for the HubSpot Contacts/Deals sync
 * (backend/hubspot.py). Automatic sync also runs every 30 min via
 * APScheduler — this card is for status visibility + an on-demand pull.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function HubspotSyncCard({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/hubspot/status");
      setStatus(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/hubspot/sync");
      toast.success(`Synced ${data.contacts} contacts, ${data.deals} deals`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  if (!status) return null;

  return (
    <div className="card-flat p-4" data-testid="hubspot-sync-card">
      <div className="flex items-center justify-between mb-1">
        <div className="font-display text-sm font-semibold text-slate-900">HubSpot CRM sync</div>
        {status.ok ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid="hubspot-status-ok"><CheckCircle2 size={12} /> Connected</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid="hubspot-status-error"><AlertTriangle size={12} /> {status.configured ? "Error" : "Not configured"}</span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-3">
        {status.configured
          ? "Contacts and Deals sync into crm_prospects / crm_deals automatically every 30 minutes. Page-view activity feeds the audit log as crm.hubspot.pageview events."
          : "Add HUBSPOT_PRIVATE_APP_TOKEN to backend/.env (Settings \u203a Integrations \u203a Private Apps in HubSpot, with crm.objects.contacts.read + crm.objects.deals.read scopes) to enable this sync."}
      </div>
      {status.error && <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-100 rounded-sm px-2 py-1.5 mb-3">{status.error}</div>}
      {status.last_sync && (
        <div className="text-[11px] text-slate-400 mb-3" data-testid="hubspot-last-sync">
          Last sync: {new Date(status.last_sync.at).toLocaleString()} — {status.last_sync.contacts} contacts, {status.last_sync.deals} deals, {status.last_sync.pageview_events || 0} page-view events
        </div>
      )}
      {isAdmin && (
        <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing || !status.configured} className="rounded-sm" data-testid="hubspot-sync-now-btn">
          {syncing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <RefreshCw size={12} className="mr-1.5" />} Sync now
        </Button>
      )}
    </div>
  );
}
